import { Component, Inject, inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SlicePipe } from '@angular/common';
import { Subject } from 'rxjs';
import { Organization, OrgHat, OrgPart, OrgSecurityGroup } from '@newgenus/common';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent } from '../../shared/org-search-bar/org-search-bar.component';
import { fuzzyMatch } from '../../shared/org-filter-utils';
import { OrgBoardService } from '../../services/org-board-service';
import { DialogService } from '../../../../services/dialog.service';
import { ProfileColorService } from '../../services/profile-color.service';

export interface HatCleanupDialogData {
    organization: Organization;
}

export type CleanupAction = 'delete' | 'removeUsers';

@Component({
    selector: 'shared-org-hat-cleanup-dialog',
    imports: [
        SlicePipe,
        OrgFilterBarComponent,
        OrgSearchBarComponent,
        MatDialogModule,
        MatTableModule,
        MatCheckboxModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatButtonModule,
        MatChipsModule,
        MatBadgeModule,
        MatIconModule,
        MatMenuModule,
        MatTabsModule,
    ],
    templateUrl: './org-hat-cleanup-dialog.html',
    styleUrls: ['./org-hat-cleanup-dialog.scss', '../org-dialog.scss'],
})
export class OrgHatCleanupDialogComponent implements OnInit, OnDestroy {
    // ── Services ───────────────────────────────────
    private obService = inject(OrgBoardService);
    private dialogService = inject(DialogService);
    private snackbar = inject(MatSnackBar);
    private profileColorService = inject(ProfileColorService);

    // ── State ──────────────────────────────────────
    public activeTabIndex = 0;
    public allHatsData: OrgHat[] = [];
    public dataSource = new MatTableDataSource<OrgHat>([]);
    public selection = new SelectionModel<OrgHat>(true, []);
    public displayedColumns = ['select', 'name', 'users', 'securityGroups', 'positions', 'actions'];
    public processing = false;
    public actionLog: string[] = [];
    public showActionLog = false;
    public brokenAvatars: Record<string, boolean> = {};

    // ── Deleted Hats State ─────────────────────────
    public allDeletedHats: OrgHat[] = [];
    public deletedDataSource = new MatTableDataSource<OrgHat>([]);
    public deletedSelection = new SelectionModel<OrgHat>(true, []);
    public deletedDisplayedColumns = ['select', 'name', 'users', 'securityGroups', 'positions', 'actions'];
    public deletedFilterConfig: OrgFilterConfig[] = [];
    public deletedRightFilterConfig: OrgFilterConfig[] = [];
    public deletedActiveFilters = new Set<string>();
    public deletedSearchTerm = '';
    public deletedSortColumn = 'name';
    public deletedSortDirection: 'asc' | 'desc' = 'asc';

    // ── Filters ────────────────────────────────────
    public filterConfig: OrgFilterConfig[] = [];
    public rightFilterConfig: OrgFilterConfig[] = [];
    public activeFilters = new Set<string>();
    public searchTerm = '';
    public sortColumn = 'name';
    public sortDirection: 'asc' | 'desc' = 'asc';

    private destroy$ = new Subject<void>();

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: HatCleanupDialogData,
        private dialogRef: MatDialogRef<OrgHatCleanupDialogComponent>
    ) {}

    ngOnInit(): void {
        this.loadHats();
        this.loadDeletedHats();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ── Data Loading ───────────────────────────────

    private loadHats(): void {
        const org = this.data.organization;
        const orgHats = org.orgHats || { hatsKeys: [] };
        const hatsKeys: string[] = orgHats.hatsKeys || [];

        this.allHatsData = hatsKeys
            .map((k) => orgHats[k])
            .filter((h: OrgHat) => h && !h.isSoftDeleted)
            .map((h: OrgHat) => ({ ...h }));

        this.buildFilterConfigs();
        this.applyFilters();
    }

    private async loadDeletedHats(): Promise<void> {
        const org = this.data.organization;
        this.allDeletedHats = await this.obService.getDeletedHats(org.key);

        this.buildDeletedFilterConfigs();
        this.applyDeletedFilters();
    }

    // ── Filter Config ──────────────────────────────

    private buildFilterConfigs(): void {
        const hats = this.allHatsData;
        this.filterConfig = [
            {
                key: 'posts',
                label: 'Posts',
                icon: 'local_post_office',
                count: hats.filter((h) => this.isHatLinkedToPost(h)).length,
            },
            {
                key: 'noPosts',
                label: 'No Posts',
                icon: 'mail_outline',
                count: hats.filter((h) => !this.isHatLinkedToPost(h)).length,
            },
            {
                key: 'specialRights',
                label: 'Special Rights',
                icon: 'verified_user',
                count: hats.filter((h) => h.isSpecialRights === true).length,
            },
            {
                key: 'noSpecialRights',
                label: 'No Special Rights',
                icon: 'remove_moderator',
                count: hats.filter((h) => !h.isSpecialRights).length,
            },
            {
                key: 'withUsers',
                label: 'With Staff',
                icon: 'people',
                count: hats.filter((h) => this.getHatUserKeys(h).length > 0 || this.getPendingUserKeys(h).length > 0)
                    .length,
            },
            {
                key: 'noUsers',
                label: 'No Staff',
                icon: 'people_outline',
                count: hats.filter(
                    (h) => this.getHatUserKeys(h).length === 0 && this.getPendingUserKeys(h).length === 0
                ).length,
            },
            {
                key: 'withSg',
                label: 'With Groups',
                icon: 'security',
                count: hats.filter((h) => this.getSecurityGroupNames(h).length > 0).length,
            },
            {
                key: 'noSg',
                label: 'No Groups',
                icon: 'shield',
                count: hats.filter((h) => this.getSecurityGroupNames(h).length === 0).length,
            },
        ];

        this.rightFilterConfig = [
            {
                key: 'deletable',
                label: 'Deletable',
                icon: 'delete_sweep',
                count: hats.filter((h) => this.isDeletable(h)).length,
            },
            {
                key: 'removableUsers',
                label: 'Removable Users',
                icon: 'person_remove',
                count: hats.filter((h) => this.hasRemovableUsers(h)).length,
            },
        ];
    }

    // ── Filter Application ─────────────────────────

    public onFiltersChanged(filters: Set<string>): void {
        this.activeFilters = filters;
        this.applyFilters();
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.applyFilters();
    }

    private applyFilters(): void {
        let result = [...this.allHatsData];
        const term = this.searchTerm?.trim().toLowerCase();

        // Text search
        if (term) {
            result = result.filter(
                (hat) =>
                    fuzzyMatch(hat.name || '', term) ||
                    fuzzyMatch(hat.description || '', term) ||
                    this.getUsersForHat(hat).some((u) => (u.name || '').toLowerCase().includes(term)) ||
                    this.getSecurityGroupNames(hat).some((sg) => sg.toLowerCase().includes(term))
            );
        }

        // Filter pills
        if (this.activeFilters.has('posts')) {
            result = result.filter((hat) => this.isHatLinkedToPost(hat));
        }
        if (this.activeFilters.has('noPosts')) {
            result = result.filter((hat) => !this.isHatLinkedToPost(hat));
        }
        if (this.activeFilters.has('specialRights')) {
            result = result.filter((hat) => hat.isSpecialRights === true);
        }
        if (this.activeFilters.has('noSpecialRights')) {
            result = result.filter((hat) => !hat.isSpecialRights);
        }
        if (this.activeFilters.has('withUsers')) {
            result = result.filter(
                (hat) => this.getHatUserKeys(hat).length > 0 || this.getPendingUserKeys(hat).length > 0
            );
        }
        if (this.activeFilters.has('noUsers')) {
            result = result.filter(
                (hat) => this.getHatUserKeys(hat).length === 0 && this.getPendingUserKeys(hat).length === 0
            );
        }
        if (this.activeFilters.has('withSg')) {
            result = result.filter((hat) => this.getSecurityGroupNames(hat).length > 0);
        }
        if (this.activeFilters.has('noSg')) {
            result = result.filter((hat) => this.getSecurityGroupNames(hat).length === 0);
        }
        if (this.activeFilters.has('deletable')) {
            result = result.filter((hat) => this.isDeletable(hat));
        }
        if (this.activeFilters.has('removableUsers')) {
            result = result.filter((hat) => this.hasRemovableUsers(hat));
        }

        // Sort
        result = this.sortHats(result);

        this.dataSource.data = result;
        // Clear selection for rows no longer visible
        const visibleKeys = new Set(result.map((h) => h.key));
        this.selection.selected.filter((h) => !visibleKeys.has(h.key)).forEach((h) => this.selection.deselect(h));
    }

    public toggleSort(column: string): void {
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.applyFilters();
    }

    private sortHats(hats: OrgHat[]): OrgHat[] {
        const dir = this.sortDirection === 'asc' ? 1 : -1;
        return hats.sort((a, b) => {
            switch (this.sortColumn) {
                case 'name':
                    return (a.name || '').localeCompare(b.name || '') * dir;
                case 'users': {
                    const ac = this.getHatUserKeys(a).length;
                    const bc = this.getHatUserKeys(b).length;
                    return (ac - bc) * dir;
                }
                case 'securityGroups': {
                    const ac = this.getSecurityGroupNames(a).length;
                    const bc = this.getSecurityGroupNames(b).length;
                    return (ac - bc) * dir;
                }
                case 'positions': {
                    const ac = this.getPositionNames(a).length;
                    const bc = this.getPositionNames(b).length;
                    return (ac - bc) * dir;
                }
                default:
                    return 0;
            }
        });
    }

    // ── Selection Helpers ──────────────────────────

    public isAllSelected(): boolean {
        return this.selection.selected.length === this.dataSource.data.length && this.dataSource.data.length > 0;
    }

    public toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.selection.select(...this.dataSource.data);
        }
    }

    // ── Classification Helpers ─────────────────────

    /** Hat can be deleted: no users, no posts, not special rights */
    public isDeletable(hat: OrgHat): boolean {
        return this.getHatUserKeys(hat).length === 0 && !this.isHatLinkedToPost(hat) && !hat.isSpecialRights;
    }

    /** Hat has users that can be removed: has users, no posts, not special rights */
    public hasRemovableUsers(hat: OrgHat): boolean {
        return this.getHatUserKeys(hat).length > 0 && !this.isHatLinkedToPost(hat) && !hat.isSpecialRights;
    }

    // ── Bulk Actions ───────────────────────────────

    public async bulkDelete(): Promise<void> {
        const selected = this.selection.selected;
        if (selected.length === 0) return;

        const risky = selected.filter((h) => !this.isDeletable(h));

        let message = `Delete ${selected.length} hat(s)? This will soft-delete them.`;
        if (risky.length > 0) {
            message +=
                `\n\n⚠️ ${risky.length} hat(s) have users, posts, or special rights:\n` +
                risky.map((h) => `  • ${h.name}`).join('\n');
        }

        const confirmed = await this.dialogService.asyncConfirm('Delete Hats', message);
        if (!confirmed) return;

        this.processing = true;
        this.actionLog = [];
        this.showActionLog = true;
        let successCount = 0;

        for (const hat of selected) {
            try {
                const hatData = { ...hat, isSoftDeleted: true } as OrgHat;
                const result = await this.obService.saveHat(hatData);
                if (result.succeeded) {
                    successCount++;
                    this.actionLog.push(`Deleted "${hat.name}"`);
                    // Move from active to deleted list
                    this.allHatsData = this.allHatsData.filter((h) => h.key !== hat.key);
                    hat.isSoftDeleted = true;
                    this.allDeletedHats.push(hat);
                } else {
                    this.actionLog.push(`Failed: "${hat.name}"`);
                }
            } catch {
                this.actionLog.push(`Error: "${hat.name}"`);
            }
        }

        this.selection.clear();
        this.buildFilterConfigs();
        this.applyFilters();
        this.buildDeletedFilterConfigs();
        this.applyDeletedFilters();
        this.processing = false;
        this.snackbar.open(`Deleted ${successCount} of ${selected.length} hat(s)`, 'OK', { duration: 4000 });
    }

    public async bulkRemoveUsers(): Promise<void> {
        const selected = this.selection.selected;
        if (selected.length === 0) return;

        const withUsers = selected.filter(
            (h) => this.getHatUserKeys(h).length > 0 || this.getPendingUserKeys(h).length > 0
        );
        if (withUsers.length === 0) {
            this.snackbar.open('None of the selected hats have users.', 'OK', { duration: 5000 });
            return;
        }

        const totalUsers = withUsers.reduce((sum, h) => sum + this.getHatUserKeys(h).length, 0);
        const withPosts = withUsers.filter((h) => this.isHatLinkedToPost(h));
        const withSpecial = withUsers.filter((h) => h.isSpecialRights);

        let message = `Remove users from ${withUsers.length} hat(s)? (${totalUsers} user assignment(s) will be cleared)`;
        if (withPosts.length > 0 || withSpecial.length > 0) {
            message += `\n\n⚠️ This includes hats with:`;
            if (withPosts.length > 0) message += `\n  • ${withPosts.length} hat(s) linked to posts`;
            if (withSpecial.length > 0) message += `\n  • ${withSpecial.length} hat(s) with special rights`;
        }

        const confirmed = await this.dialogService.asyncConfirm('Remove Users from Hats', message);
        if (!confirmed) return;

        this.processing = true;
        this.actionLog = [];
        this.showActionLog = true;
        let successCount = 0;

        for (const hat of withUsers) {
            try {
                const userCount = this.getHatUserKeys(hat).length;
                const pendingCount = this.getPendingUserKeys(hat).length;
                // NOTE: `assigned` and `userKeys` are deprecated per
                // docs/RETIRE-HAT-ASSIGNED-AND-USERKEYS.md — don't write them.
                // Backend (processHats.f.ts) only reads assignedToArray.
                const hatData: OrgHat = {
                    ...hat,
                    assignedToArray: [],
                    userKeys: [],
                };
                (hatData as OrgHat).pendingAssignedToArray = [];
                const result = await this.obService.saveHat(hatData);
                if (result.succeeded) {
                    successCount++;
                    const parts: string[] = [];
                    if (userCount > 0) parts.push(`${userCount} user(s)`);
                    if (pendingCount > 0) parts.push(`${pendingCount} pending`);
                    this.actionLog.push(`Removed ${parts.join(' + ')} from "${hat.name}"`);
                    // Update local data
                    const localHat = this.allHatsData.find((h) => h.key === hat.key);
                    if (localHat) {
                        localHat.assignedToArray = [];
                        localHat.userKeys = [];
                        (localHat as OrgHat).pendingAssignedToArray = [];
                    }
                } else {
                    this.actionLog.push(`Failed: "${hat.name}"`);
                }
            } catch {
                this.actionLog.push(`Error: "${hat.name}"`);
            }
        }

        this.selection.clear();
        this.buildFilterConfigs();
        this.applyFilters();
        this.processing = false;
        this.snackbar.open(`Cleaned ${successCount} of ${withUsers.length} hat(s)`, 'OK', { duration: 4000 });
    }

    // ── Single-Row Actions ─────────────────────────

    public async singleDelete(hat: OrgHat): Promise<void> {
        this.selection.clear();
        this.selection.select(hat);
        await this.bulkDelete();
    }

    public async singleRemoveUsers(hat: OrgHat): Promise<void> {
        this.selection.clear();
        this.selection.select(hat);
        await this.bulkRemoveUsers();
    }

    // ── Hat Data Helpers ───────────────────────────

    public getHatUserKeys(hat: OrgHat): string[] {
        return hat.assignedToArray || hat.userKeys || [];
    }

    public getPendingUserKeys(hat: OrgHat): string[] {
        const approved = this.getHatUserKeys(hat);
        const pending: string[] = hat.pendingAssignedToArray || [];
        return pending.filter((k) => !approved.includes(k));
    }

    public getUsersForHat(
        hat: OrgHat
    ): { key: string; name: string; photoURL?: string; photoThumbnail?: string; profileColor?: string }[] {
        const userKeys = this.getHatUserKeys(hat);
        const orgUsers = this.data.organization?.orgUsers;
        if (!orgUsers) return [];
        return userKeys
            .filter((k: string) => orgUsers[k])
            .map((k: string) => ({
                key: k,
                name: orgUsers[k].name || orgUsers[k].userName || k,
                photoURL: orgUsers[k].photoURL,
                photoThumbnail: orgUsers[k].photoThumbnail,
                profileColor: this.profileColorService.getColor(k),
            }));
    }

    public getPendingUsersForHat(
        hat: OrgHat
    ): { key: string; name: string; photoURL?: string; photoThumbnail?: string; profileColor?: string }[] {
        const pendingKeys = this.getPendingUserKeys(hat);
        const orgUsers = this.data.organization?.orgUsers;
        if (!orgUsers || pendingKeys.length === 0) return [];
        return pendingKeys
            .filter((k) => orgUsers[k])
            .map((k) => ({
                key: k,
                name: orgUsers[k].name || orgUsers[k].userName || k,
                photoURL: orgUsers[k].photoURL,
                photoThumbnail: orgUsers[k].photoThumbnail,
                profileColor: this.profileColorService.getColor(k),
            }));
    }

    public getUserNamesTooltip(hat: OrgHat): string {
        const approved = this.getUsersForHat(hat).map((u) => u.name);
        const pending = this.getPendingUsersForHat(hat).map((u) => `${u.name} (pending)`);
        return [...approved, ...pending].join(', ');
    }

    public getInitials(name: string): string {
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2
            ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
            : name.substring(0, 2).toUpperCase();
    }

    public getSecurityGroupNames(hat: OrgHat): string[] {
        const sgs = hat.securityGroupsArray || hat.securityGroups || [];
        const orgSgs = this.data.organization?.orgSecurityGroups || { securityGroupsKeys: [] };
        return sgs
            .map((sg: string | OrgSecurityGroup) => {
                const key = typeof sg === 'string' ? sg : sg?.key;
                return orgSgs[key]?.name || key;
            })
            .filter(Boolean);
    }

    public getPositionNames(hat: OrgHat): string[] {
        const posArr: string[] = hat.positionArray || [];
        if (posArr.length === 0) return [];
        const orgParts: OrgPart[] = this.data.organization?.orgParts || [];
        return posArr
            .map((pKey: string) => {
                const part = orgParts.find((p: OrgPart) => p.key === pKey || p.partKey === pKey);
                return part?.name || pKey;
            })
            .filter(Boolean);
    }

    public isHatLinkedToPost(hat: OrgHat): boolean {
        if (!hat.positionArray || !Array.isArray(hat.positionArray)) return false;
        const orgParts: OrgPart[] = this.data.organization?.orgParts || [];
        return hat.positionArray.some((partKey: string) => {
            const part = orgParts.find((p: OrgPart) => p.key === partKey || p.partKey === partKey);
            if (!part) return false;
            return this.resolvePartType(part) === 'post';
        });
    }

    private resolvePartType(part: OrgPart): string {
        if (part.partType) return part.partType.toLowerCase();
        const depth = part.depth || 0;
        if (depth <= 1) return 'executive';
        if (depth === 2) return 'division';
        if (depth === 3) return 'department';
        if (depth === 4) return 'section';
        if (depth === 5) return 'unit';
        return 'post';
    }

    public isPositionless(hat: OrgHat): boolean {
        const posArr: string[] = hat.positionArray || [];
        return posArr.length === 0;
    }

    public getPositionTooltip(hat: OrgHat): string {
        return this.getPositionNames(hat).join(', ');
    }

    /** Get a summary badge class for a hat based on its cleanup status */
    public getCleanupStatus(hat: OrgHat): 'deletable' | 'removable-users' | 'ok' {
        if (this.isDeletable(hat)) return 'deletable';
        if (this.hasRemovableUsers(hat)) return 'removable-users';
        return 'ok';
    }

    // ══════════════════════════════════════════════
    // ── Deleted Hats Tab ─────────────────────────
    // ══════════════════════════════════════════════

    public onTabChanged(index: number): void {
        this.activeTabIndex = index;
        this.selection.clear();
        this.deletedSelection.clear();
    }

    // ── Deleted Filter Config ──────────────────────

    private buildDeletedFilterConfigs(): void {
        const hats = this.allDeletedHats;
        this.deletedFilterConfig = [
            {
                key: 'posts',
                label: 'Posts',
                icon: 'local_post_office',
                count: hats.filter((h) => this.isHatLinkedToPost(h)).length,
            },
            {
                key: 'noPosts',
                label: 'No Posts',
                icon: 'mail_outline',
                count: hats.filter((h) => !this.isHatLinkedToPost(h)).length,
            },
            {
                key: 'specialRights',
                label: 'Special Rights',
                icon: 'verified_user',
                count: hats.filter((h) => h.isSpecialRights === true).length,
            },
            {
                key: 'noSpecialRights',
                label: 'No Special Rights',
                icon: 'remove_moderator',
                count: hats.filter((h) => !h.isSpecialRights).length,
            },
            {
                key: 'withUsers',
                label: 'With Staff',
                icon: 'people',
                count: hats.filter((h) => this.getHatUserKeys(h).length > 0 || this.getPendingUserKeys(h).length > 0)
                    .length,
            },
            {
                key: 'noUsers',
                label: 'No Staff',
                icon: 'people_outline',
                count: hats.filter(
                    (h) => this.getHatUserKeys(h).length === 0 && this.getPendingUserKeys(h).length === 0
                ).length,
            },
            {
                key: 'withSg',
                label: 'With Groups',
                icon: 'security',
                count: hats.filter((h) => this.getSecurityGroupNames(h).length > 0).length,
            },
            {
                key: 'noSg',
                label: 'No Groups',
                icon: 'shield',
                count: hats.filter((h) => this.getSecurityGroupNames(h).length === 0).length,
            },
        ];
        this.deletedRightFilterConfig = [];
    }

    // ── Deleted Filter Application ─────────────────

    public onDeletedFiltersChanged(filters: Set<string>): void {
        this.deletedActiveFilters = filters;
        this.applyDeletedFilters();
    }

    public onDeletedSearchChanged(term: string): void {
        this.deletedSearchTerm = term;
        this.applyDeletedFilters();
    }

    private applyDeletedFilters(): void {
        let result = [...this.allDeletedHats];
        const term = this.deletedSearchTerm?.trim().toLowerCase();

        if (term) {
            result = result.filter(
                (hat) =>
                    fuzzyMatch(hat.name || '', term) ||
                    fuzzyMatch(hat.description || '', term) ||
                    this.getUsersForHat(hat).some((u) => (u.name || '').toLowerCase().includes(term)) ||
                    this.getSecurityGroupNames(hat).some((sg) => sg.toLowerCase().includes(term))
            );
        }

        if (this.deletedActiveFilters.has('posts')) {
            result = result.filter((hat) => this.isHatLinkedToPost(hat));
        }
        if (this.deletedActiveFilters.has('noPosts')) {
            result = result.filter((hat) => !this.isHatLinkedToPost(hat));
        }
        if (this.deletedActiveFilters.has('specialRights')) {
            result = result.filter((hat) => hat.isSpecialRights === true);
        }
        if (this.deletedActiveFilters.has('noSpecialRights')) {
            result = result.filter((hat) => !hat.isSpecialRights);
        }
        if (this.deletedActiveFilters.has('withUsers')) {
            result = result.filter(
                (hat) => this.getHatUserKeys(hat).length > 0 || this.getPendingUserKeys(hat).length > 0
            );
        }
        if (this.deletedActiveFilters.has('noUsers')) {
            result = result.filter(
                (hat) => this.getHatUserKeys(hat).length === 0 && this.getPendingUserKeys(hat).length === 0
            );
        }
        if (this.deletedActiveFilters.has('withSg')) {
            result = result.filter((hat) => this.getSecurityGroupNames(hat).length > 0);
        }
        if (this.deletedActiveFilters.has('noSg')) {
            result = result.filter((hat) => this.getSecurityGroupNames(hat).length === 0);
        }

        result = this.sortDeletedHats(result);

        this.deletedDataSource.data = result;
        const visibleKeys = new Set(result.map((h) => h.key));
        this.deletedSelection.selected
            .filter((h) => !visibleKeys.has(h.key))
            .forEach((h) => this.deletedSelection.deselect(h));
    }

    public toggleDeletedSort(column: string): void {
        if (this.deletedSortColumn === column) {
            this.deletedSortDirection = this.deletedSortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.deletedSortColumn = column;
            this.deletedSortDirection = 'asc';
        }
        this.applyDeletedFilters();
    }

    private sortDeletedHats(hats: OrgHat[]): OrgHat[] {
        const dir = this.deletedSortDirection === 'asc' ? 1 : -1;
        return hats.sort((a, b) => {
            switch (this.deletedSortColumn) {
                case 'name':
                    return (a.name || '').localeCompare(b.name || '') * dir;
                case 'users': {
                    const ac = this.getHatUserKeys(a).length;
                    const bc = this.getHatUserKeys(b).length;
                    return (ac - bc) * dir;
                }
                case 'securityGroups': {
                    const ac = this.getSecurityGroupNames(a).length;
                    const bc = this.getSecurityGroupNames(b).length;
                    return (ac - bc) * dir;
                }
                case 'positions': {
                    const ac = this.getPositionNames(a).length;
                    const bc = this.getPositionNames(b).length;
                    return (ac - bc) * dir;
                }
                default:
                    return 0;
            }
        });
    }

    // ── Deleted Selection Helpers ───────────────────

    public isAllDeletedSelected(): boolean {
        return (
            this.deletedSelection.selected.length === this.deletedDataSource.data.length &&
            this.deletedDataSource.data.length > 0
        );
    }

    public toggleAllDeletedRows(): void {
        if (this.isAllDeletedSelected()) {
            this.deletedSelection.clear();
        } else {
            this.deletedSelection.select(...this.deletedDataSource.data);
        }
    }

    // ── Restore Actions ────────────────────────────

    public async bulkRestore(): Promise<void> {
        const selected = this.deletedSelection.selected;
        if (selected.length === 0) return;

        const confirmed = await this.dialogService.asyncConfirm(
            'Restore Hats',
            `Restore ${selected.length} hat(s)? They will become active again.`
        );
        if (!confirmed) return;

        this.processing = true;
        this.actionLog = [];
        this.showActionLog = true;
        let successCount = 0;

        for (const hat of selected) {
            try {
                const hatData = { ...hat, isSoftDeleted: false } as OrgHat;
                const result = await this.obService.saveHat(hatData);
                if (result.succeeded) {
                    successCount++;
                    this.actionLog.push(`Restored "${hat.name}"`);
                    // Move from deleted to active
                    this.allDeletedHats = this.allDeletedHats.filter((h) => h.key !== hat.key);
                    hat.isSoftDeleted = false;
                    this.allHatsData.push(hat);
                } else {
                    this.actionLog.push(`Failed: "${hat.name}"`);
                }
            } catch {
                this.actionLog.push(`Error: "${hat.name}"`);
            }
        }

        this.deletedSelection.clear();
        this.buildFilterConfigs();
        this.applyFilters();
        this.buildDeletedFilterConfigs();
        this.applyDeletedFilters();
        this.processing = false;
        this.snackbar.open(`Restored ${successCount} of ${selected.length} hat(s)`, 'OK', { duration: 4000 });
    }

    public async singleRestore(hat: OrgHat): Promise<void> {
        this.deletedSelection.clear();
        this.deletedSelection.select(hat);
        await this.bulkRestore();
    }
}
